/**
 * CoachCollaboratorInsights.jsx — P2 Iter239
 * ============================================
 * Côté coach : liste les collaborateurs (clients) avec un Growth Path
 * accessible (booking actif) + vue détaillée read-only d'un parcours.
 *
 * Accès strictement gated par le backend (403 si pas de booking actif).
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Loader2, Shield, Sparkles, Target } from 'lucide-react';
import { getApiUrl } from '../../config/environment';

const API = getApiUrl();

const authHeaders = () => {
  const t = localStorage.getItem('token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const STATUS_LABEL = {
  confirmed: 'Confirmé',
  completed: 'Terminé',
  report_generated: 'Rapport généré',
  validated: 'Validé',
  to_pay: 'À payer',
  coach_paid: 'Payé',
};

export default function CoachCollaboratorInsights() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const loadList = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/coach/collaborators`, { headers: authHeaders() });
      setItems(r.data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Chargement impossible');
    } finally {
      setLoading(false);
    }
  };

  const loadDetail = async (item) => {
    setSelected(item);
    setDetail(null);
    setDetailLoading(true);
    try {
      const cid = item.collaborator_id || item.user_id;
      const r = await axios.get(
        `${API}/coach/collaborators/${encodeURIComponent(cid)}/growth-path`,
        { headers: authHeaders() },
      );
      setDetail(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Accès au parcours refusé');
      setSelected(null);
    } finally {
      setDetailLoading(false);
    }
  };

  useEffect(() => { loadList(); }, []);

  if (selected && (detail || detailLoading)) {
    const gp = detail?.growth_path || {};
    const axes = gp.axes_priorises || [];
    const comps = gp.competences_a_developper || [];
    const ctx = detail?.access_context || {};
    return (
      <div className="min-h-screen bg-stone-50" data-testid="coach-insights-detail">
        <div className="bg-[#0F0F10] text-white py-5">
          <div className="max-w-4xl mx-auto px-6 flex items-center gap-3">
            <Button variant="ghost" size="sm" className="text-white hover:bg-white/10"
                    onClick={() => { setSelected(null); setDetail(null); }}
                    data-testid="coach-insights-back">
              <ArrowLeft className="w-4 h-4 mr-1" /> Retour
            </Button>
            <div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#92710A]">Coach · COACHDIA Prism</div>
              <h1 className="font-serif text-2xl">
                Parcours de {selected.display_name || selected.email || '—'}
              </h1>
            </div>
          </div>
        </div>
        <div className="max-w-4xl mx-auto px-6 py-8 space-y-6">
          {detailLoading && (
            <div className="flex items-center gap-2 text-stone-500 py-16 justify-center">
              <Loader2 className="w-5 h-5 animate-spin" /> Chargement...
            </div>
          )}
          {detail && (
            <>
              <Card className="p-4 border-l-4 border-l-[#2D8659] flex items-center gap-3"
                    data-testid="coach-insights-access-badge">
                <Shield className="w-4 h-4 text-[#2D8659]" />
                <div className="text-sm text-stone-700">
                  Accès autorisé via booking <strong>{STATUS_LABEL[ctx.via_booking_status] || ctx.via_booking_status}</strong>
                  {ctx.via_booking_date && <> · {ctx.via_booking_date}</>}
                </div>
              </Card>

              {gp.archetype_name && (
                <Card className="p-5" data-testid="coach-insights-archetype">
                  <div className="text-[10px] tracking-[0.3em] uppercase text-stone-400">Archétype</div>
                  <div className="font-serif text-2xl text-[#0F0F10] mt-1">{gp.archetype_name}</div>
                  {gp.dominant_color && (
                    <div className="text-sm text-stone-500 mt-1">
                      Dominant : <span className="font-medium">{gp.dominant_color}</span>
                      {gp.secondary_color && <> · Secondaire : {gp.secondary_color}</>}
                    </div>
                  )}
                </Card>
              )}

              {axes.length > 0 && (
                <Card className="p-5" data-testid="coach-insights-axes">
                  <div className="flex items-center gap-2 mb-3">
                    <Target className="w-4 h-4 text-[#92710A]" />
                    <div className="text-[10px] tracking-[0.3em] uppercase text-stone-400">Axes priorisés</div>
                  </div>
                  <div className="space-y-3">
                    {axes.map((a, i) => (
                      <div key={i} className="p-3 bg-[#FBF8F1] border-l-2 border-[#92710A]">
                        <div className="text-[10px] uppercase tracking-widest text-[#92710A]">Axe {i + 1}</div>
                        <div className="text-sm text-stone-800 mt-1">{a}</div>
                      </div>
                    ))}
                  </div>
                </Card>
              )}

              {comps.length > 0 && (
                <Card className="p-5" data-testid="coach-insights-competencies">
                  <div className="flex items-center gap-2 mb-3">
                    <Sparkles className="w-4 h-4 text-[#92710A]" />
                    <div className="text-[10px] tracking-[0.3em] uppercase text-stone-400">Compétences à développer</div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {comps.map((c, i) => (
                      <Badge key={i} variant="outline" className="text-sm">
                        {typeof c === 'string' ? c : (c.name || '—')}
                      </Badge>
                    ))}
                  </div>
                </Card>
              )}
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50" data-testid="coach-insights-list">
      <div className="bg-[#0F0F10] text-white py-5">
        <div className="max-w-4xl mx-auto px-6 flex items-start gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/coach/dashboard')}
            data-testid="coach-insights-list-back"
            className="text-white hover:bg-white/10 -ml-2 mt-0.5 shrink-0"
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> Retour
          </Button>
          <div className="min-w-0">
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#92710A]">Coach · COACHDIA Prism</div>
            <h1 className="font-serif text-2xl">Mes clients COACHDIA Prism</h1>
            <p className="text-xs text-stone-300 mt-1">
              Uniquement les clients avec un booking <strong>confirmé</strong> ou <strong>terminé</strong>
            </p>
          </div>
        </div>
      </div>
      <div className="max-w-4xl mx-auto px-6 py-8 space-y-4">
        {loading && (
          <div className="flex items-center gap-2 text-stone-500 py-16 justify-center">
            <Loader2 className="w-5 h-5 animate-spin" /> Chargement...
          </div>
        )}
        {!loading && items.length === 0 && (
          <Card className="p-6 text-sm text-stone-500" data-testid="coach-insights-empty">
            Aucun client COACHDIA Prism accessible pour le moment.
            Un accès s'ouvre dès qu'un booking est confirmé avec un collaborateur ayant un parcours généré.
          </Card>
        )}
        {items.map((it) => (
          <Card key={it.growth_path_id} className="p-4 flex items-center justify-between"
                data-testid={`coach-insights-item-${it.growth_path_id}`}>
            <div>
              <div className="font-semibold text-stone-900">
                {it.display_name || it.email || it.user_id}
              </div>
              <div className="text-xs text-stone-500 mt-0.5 space-x-2">
                {it.archetype_name && <span>Archétype : <strong>{it.archetype_name}</strong></span>}
                {it.last_booking_status && (
                  <span>· Booking : {STATUS_LABEL[it.last_booking_status] || it.last_booking_status}</span>
                )}
                {it.last_booking_date && <span>· {it.last_booking_date}</span>}
              </div>
            </div>
            <Button size="sm" onClick={() => loadDetail(it)}
                    data-testid={`coach-insights-open-${it.growth_path_id}`}>
              Consulter le parcours
            </Button>
          </Card>
        ))}
      </div>
    </div>
  );
}
